import { ActionReference } from "@shared/types";
import styles from "../../styles";

/**
 * Component for rendering travel action buttons
 */
export class TravelButtonsComponent extends HTMLElement {
    private _actions: ActionReference[] = [];
    private _selectedAction: ActionReference | null = null;
    private _onActionClick: ((action: ActionReference) => void) | null = null;

    /**
     * Set the available travel actions
     */
    public set actions(actions: ActionReference[]) {
        this._actions = actions;
        this.render();
    }

    /**
     * Set the selected action
     */
    public set selectedAction(action: ActionReference | null) {
        this._selectedAction = action;
        this.render();
    }

    /**
     * Set the action click handler
     */
    public set onActionClick(handler: (action: ActionReference) => void) {
        this._onActionClick = handler;
    }

    /**
     * Called when the element is connected to the DOM
     */
    public connectedCallback(): void {
        this.render();
        this.setupEventListeners();
    }

    /**
     * Called when the element is disconnected from the DOM
     */
    public disconnectedCallback(): void {
        this.removeEventListeners();
    }

    /**
     * Render the component
     */
    private render(): void {
        this.innerHTML = `
            <style>
                ${styles.travel}
            </style>
            <div class="travel-container">
                <h3 class="travel-title">Travel Options</h3>
                <div class="travel-buttons">
                    ${this.renderTravelButtons()}
                </div>
            </div>
        `;
    }

    /**
     * Render the travel action buttons
     */
    private renderTravelButtons(): string {
        if (this._actions.length === 0) {
            return "<p class=\"no-travel-options\">No travel options available in this location.</p>";
        }

        return this._actions
            .map(action => {
                const isSelected: boolean = this._selectedAction === action;
                const selectedClass: string = isSelected ? "selected" : "";

                return `
                    <button 
                        class="travel-button ${selectedClass}" 
                        data-action="${action.alias}"
                    >
                        ${action.name}
                    </button>
                `;
            })
            .join("");
    }

    /**
     * Setup event listeners
     */
    private setupEventListeners(): void {
        this.addEventListener("click", this.handleActionClick.bind(this));
    }

    /**
     * Remove event listeners
     */
    private removeEventListeners(): void {
        this.removeEventListener("click", this.handleActionClick.bind(this));
    }

    /**
     * Handle action button click
     */
    private handleActionClick(event: MouseEvent): void {
        const target: HTMLElement = event.target as HTMLElement;

        if (target.classList.contains("travel-button") && this._onActionClick) {
            const actionAlias: string | null = target.getAttribute("data-action");
            const action: ActionReference | undefined = actionAlias ? this._actions.find(a => a.alias === actionAlias) : undefined;

            if (action) {
                this._onActionClick(action);
            }
        }
    }
}

// Register the component
customElements.define("game-travel-buttons", TravelButtonsComponent);
